import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

interface Project {
  id: number;
  name: string;
  client_name: string;
  description: string;
  project_context: string;
  analysis_instructions: string;
  expire_date?: string;
  status: string;
  progress: number;
  documents_count: number;
  templates_count: number;
  created_at: string;
  updated_at: string;
}

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  setCurrentProject: (project: Project | null) => void;
  createProject: (data: { name: string; client_name?: string; description?: string; project_context?: string; analysis_instructions?: string; expire_date?: string }) => Promise<boolean>;
  updateProject: (id: number, data: Partial<Project>) => Promise<boolean>;
  deleteProject: (id: number) => Promise<boolean>;
  refreshProjects: () => Promise<void>;
  loading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProjectState] = useState<Project | null>(() => {
    const saved = localStorage.getItem('currentProject');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);

  const setCurrentProject = (project: Project | null) => {
    setCurrentProjectState(project);
    if (project) {
      localStorage.setItem('currentProject', JSON.stringify(project));
    } else {
      localStorage.removeItem('currentProject');
    }
  };

  const refreshProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/projects');

      if (response.data && response.data.projects) {
        const fetchedProjects = response.data.projects;
        setProjects(fetchedProjects);

        const savedProjectJSON = localStorage.getItem('currentProject');
        if (savedProjectJSON) {
          const savedProject = JSON.parse(savedProjectJSON);
          const matchingProject = fetchedProjects.find((p: Project) => p.id === savedProject.id);
          if (matchingProject) {
            setCurrentProjectState(matchingProject);
          } else if (fetchedProjects.length > 0) {
            const firstProject = fetchedProjects[0];
            setCurrentProjectState(firstProject);
            localStorage.setItem('currentProject', JSON.stringify(firstProject));
          }
        } else if (fetchedProjects.length > 0) {
          const firstProject = fetchedProjects[0];
          setCurrentProjectState(firstProject);
          localStorage.setItem('currentProject', JSON.stringify(firstProject));
        }
      } else {
        console.warn('No projects data in response:', response.data);
        setProjects([]);
      }
    } catch (error: any) {
      console.error('Projects API error:', error);
      if (error.response?.status >= 400) {
        toast.error('Failed to load projects');
      }
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const createProject = async (data: { name: string; client_name?: string; description?: string; project_context?: string; analysis_instructions?: string; expire_date?: string }): Promise<boolean> => {
    try {
      const response = await api.post('/api/projects', data);
      await refreshProjects();
      // Set the new project as current
      if (response.data.project) {
        setCurrentProject(response.data.project);
      } else {
        setProjects(prevProjects => {
          setCurrentProject(prevProjects.length > 0 ? prevProjects[prevProjects.length - 1] : null);
          return prevProjects;
        });
      }
      toast.success('Project created successfully');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create project';
      toast.error(message);
      return false;
    }
  };

  const updateProject = async (id: number, data: Partial<Project>): Promise<boolean> => {
    try {
      await api.put(`/api/projects/${id}`, data);
      await refreshProjects();
      toast.success('Project updated successfully');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update project';
      toast.error(message);
      return false;
    }
  };

  const deleteProject = async (id: number): Promise<boolean> => {
    try {
      await api.delete(`/api/projects/${id}`);
      await refreshProjects();
      if (currentProject?.id === id) {
        setCurrentProject(projects.length > 1 ? projects[0] : null);
      }
      toast.success('Project deleted successfully');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to delete project';
      toast.error(message);
      return false;
    }
  };

  const value = {
    projects,
    currentProject,
    setCurrentProject,
    createProject,
    updateProject,
    deleteProject,
    refreshProjects,
    loading
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};